/**
 * Generic export state management hook with session storage persistence
 * Provides consistent state management for all export types
 */

import { useState, useEffect, useCallback } from 'react'

export interface ExportStateConfig<T> {
  exportType: string
  defaultState: T
  storageVersion?: number // For future migrations
}

export interface ExportStateReturn<T> {
  state: T
  updateState: (partial: Partial<T>) => void
  setState: (newState: T) => void
  resetToDefaults: () => void
  hasStoredState: boolean
}

/**
 * Generic hook for managing export state with session storage persistence
 * 
 * @param config Configuration object with export type and default state
 * @returns State management interface
 * 
 * @example
 * ```typescript
 * const { state, updateState, resetToDefaults } = useExportState({
 *   exportType: 'lightspeed_sales',
 *   defaultState: { dateFrom: '2024-01-01', dateTo: '2024-01-31', includeLineItems: false }
 * })
 * 
 * // Update specific fields
 * updateState({ dateFrom: '2024-02-01' })
 * 
 * // Reset to defaults
 * resetToDefaults()
 * ```
 */
export function useExportState<T extends Record<string, any>>(
  config: ExportStateConfig<T>
): ExportStateReturn<T> {
  const { exportType, defaultState, storageVersion = 1 } = config
  
  // Create storage key with version for potential migrations
  const storageKey = `export_state_${exportType}_v${storageVersion}`
  
  const [state, setStateInternal] = useState<T>(defaultState)
  const [hasStoredState, setHasStoredState] = useState(false)
  const [isLoading, setIsLoading] = useState(true) // Prevent saves during initial load

  // Load state from session storage on mount
  useEffect(() => {
    try {
      // Handle migration from old storage keys
      const legacyKeys = [`sales_export_state`] // Add other legacy keys here if needed
      let migratedData: T | null = null
      
      // Check for legacy keys and migrate if found
      if (exportType === 'lightspeed_sales') {
        const legacyData = sessionStorage.getItem('sales_export_state')
        if (legacyData) {
          try {
            migratedData = JSON.parse(legacyData) as T
            sessionStorage.removeItem('sales_export_state') // Clean up old key
          } catch (e) {
            console.warn('Failed to migrate legacy sales export state:', e)
          }
        }
      }

      const stored = sessionStorage.getItem(storageKey)
      const dataToUse = stored || (migratedData ? JSON.stringify(migratedData) : null)
      
      if (dataToUse) {
        const parsedState = typeof dataToUse === 'string' ? JSON.parse(dataToUse) as T : dataToUse
        
        // Validate that parsed state has same keys as default state (basic validation)
        const defaultKeys = Object.keys(defaultState)
        const isValidState = defaultKeys.every(key => key in parsedState)
        
        if (isValidState) {
          // Smart merge: only add missing keys from defaults, preserve all stored values
          const mergedState: T = { ...defaultState }
          Object.keys(parsedState).forEach(key => {
            mergedState[key as keyof T] = parsedState[key]
          })
          
          setStateInternal(mergedState)
          setHasStoredState(true)
        } else {
          console.warn(`Invalid stored state for ${exportType}, using defaults`)
          setHasStoredState(false)
        }
      } else {
        setHasStoredState(false)
      }
    } catch (error) {
      console.warn(`Failed to load export state for ${exportType}:`, error)
      setHasStoredState(false)
    } finally {
      // Mark loading as complete to allow saves
      setIsLoading(false)
    }
  }, [exportType, storageKey, defaultState])

  // Save state to session storage whenever it changes (but not during initial load)
  useEffect(() => {
    if (isLoading) {
      return
    }
    
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    } catch (error) {
      console.warn(`Failed to save export state for ${exportType}:`, error)
    }
  }, [state, storageKey, exportType, isLoading])

  // Update partial state (merge with existing)
  const updateState = useCallback((partial: Partial<T>) => {
    setStateInternal(prev => ({ ...prev, ...partial }))
  }, [])

  // Replace entire state
  const setState = useCallback((newState: T) => {
    setStateInternal(newState)
  }, [])

  // Reset to default state
  const resetToDefaults = useCallback(() => {
    setStateInternal(defaultState)
    setHasStoredState(false)
    try {
      sessionStorage.removeItem(storageKey)
    } catch (error) {
      console.warn(`Failed to clear export state for ${exportType}:`, error)
    }
  }, [defaultState, storageKey, exportType])

  return {
    state,
    updateState,
    setState,
    resetToDefaults,
    hasStoredState
  }
}